import { apiClient, apiCallWithToast } from './api';

/**
 * Dashboard API Service
 * Handles all dashboard-related API calls with role-based analytics
 */
export const dashboardAPI = {
  /**
   * Get dashboard analytics based on user role
   * @param {Object} params - Optional parameters
   * @param {number} params.academic_id - Specific academic year ID
   * @returns {Promise} API response with role-based dashboard data
   */
  getAnalytics: (params = {}) => {
    return apiClient.get('dashboard/analytics', { params });
  },

  /**
   * Get dashboard analytics with toast notifications
   * @param {Object} params - Optional parameters
   * @param {Object} customMessages - Custom toast messages
   * @returns {Promise} API response with role-based dashboard data
   */
  getAnalyticsWithToast: (params = {}, customMessages = {}) => {
    const defaultMessages = {
      loading: 'Loading dashboard analytics...',
      success: 'Dashboard data loaded successfully',
      error: 'Failed to load dashboard data'
    };

    return apiCallWithToast(
      () => dashboardAPI.getAnalytics(params),
      { ...defaultMessages, ...customMessages }
    );
  }
};

/**
 * Dashboard helper functions
 */
export const dashboardHelpers = {
  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency symbol
   * @returns {string} Formatted currency string
   */
  formatCurrency: (amount, currency = '₹') => {
    if (amount >= 100000) {
      return `${currency}${(amount / 100000).toFixed(2)}L`;
    } else if (amount >= 1000) {
      return `${currency}${(amount / 1000).toFixed(1)}K`;
    } else {
      return `${currency}${amount.toLocaleString()}`;
    }
  },

  /**
   * Format number for display
   * @param {number} num - Number to format
   * @returns {string} Formatted number string
   */
  formatNumber: (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  },

  /**
   * Get trend icon class based on trend value
   * @param {number} trend - Trend percentage
   * @returns {string} CSS class for trend icon
   */
  getTrendClass: (trend) => {
    if (trend === null || trend === undefined) return '';
    return trend > 0 ? 'trend-up' : 'trend-down';
  },

  /**
   * Format trend text for display
   * @param {number} trend - Trend percentage
   * @returns {string} Formatted trend text
   */
  formatTrendText: (trend) => {
    if (trend === null || trend === undefined) return '';
    const prefix = trend > 0 ? '+' : '';
    return `${prefix}${trend}%`;
  },

  /**
   * Get status badge class based on status
   * @param {string} status - Status value
   * @returns {string} CSS class for status badge
   */
  getStatusBadgeClass: (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'overdue': return 'badge-danger';
      case 'active': return 'badge-success';
      case 'inactive': return 'badge-secondary';
      default: return 'badge-primary';
    }
  },

  /**
   * Process financial data for charts
   * @param {Array} data - Raw financial data
   * @returns {Array} Processed data for charts
   */
  processFinancialData: (data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      ...item,
      received: item.received || 0,
      expenses: item.expenses || 0,
      pending: item.pending || 0
    }));
  },

  /**
   * Process attendance data for charts
   * @param {Array} data - Raw attendance data
   * @returns {Array} Processed data for charts
   */
  processAttendanceData: (data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      ...item,
      present: item.present || 0,
      absent: item.absent || 0,
      total: (item.present || 0) + (item.absent || 0)
    }));
  },

  /**
   * Calculate attendance percentage
   * @param {number} present - Present count
   * @param {number} total - Total count
   * @returns {number} Attendance percentage
   */
  calculateAttendancePercentage: (present, total) => {
    if (!total || total === 0) return 0;
    return Math.round((present / total) * 100);
  },

  /**
   * Get default stats structure for different roles
   * @param {string} role - User role
   * @returns {Array} Default stats array
   */
  getDefaultStats: (role) => {
    const defaults = {
      admin: [
        { title: 'Total Students', count: 0, color: 'primary', subtitle: 'Active enrollments' },
        { title: 'Total Teachers', count: 0, color: 'success', subtitle: 'Faculty members' },
        { title: 'Total Parents', count: 0, color: 'info', subtitle: 'Registered guardians' },
        { title: 'Revenue This Month', count: '₹0', color: 'warning', subtitle: 'Fee collections' }
      ],
      teacher: [
        { title: 'Assigned Classes', count: 0, color: 'primary', subtitle: 'Active classes' },
        { title: 'Total Students', count: 0, color: 'success', subtitle: 'Under supervision' },
        { title: 'Attendance', count: '0%', color: 'info', subtitle: 'This month' }
      ],
      student: [
        { title: 'Attendance', count: '0%', color: 'success', subtitle: 'This month' },
        { title: 'Fee Status', count: 'N/A', color: 'warning', subtitle: 'Current status' }
      ],
      accountant: [
        { title: 'Monthly Collection', count: '₹0', color: 'success', subtitle: 'This month' },
        { title: 'Outstanding Fees', count: '₹0', color: 'warning', subtitle: 'Pending collection' },
        { title: 'Transactions', count: 0, color: 'info', subtitle: 'This month' }
      ]
    };

    return defaults[role] || defaults.admin;
  }
};

export default dashboardAPI;
